import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { activeDistrictStore, resolveInitialActiveDistrict } from './activeDistrictStore'
import { useCurrentUser } from './useCurrentUser'

function storageKey(userId: string): string {
  return `vr.activeDistrictId.${userId}`
}

/**
 * Exposes the active district id and a setter. Self-initializing: once the
 * current user loads, if the store is empty or holds a district the user is no
 * longer a member of, it resolves an initial value from localStorage (else the
 * first-admin-else-first membership) and stores it. The setter persists per-user.
 */
export function useActiveDistrict(): { activeDistrictId: string | null; setActiveDistrict: (id: string) => void } {
  const { data: user } = useCurrentUser()
  const activeDistrictId = useSyncExternalStore(
    activeDistrictStore.subscribe,
    activeDistrictStore.snapshot,
    activeDistrictStore.snapshot,
  )

  useEffect(() => {
    if (!user) return
    const current = activeDistrictStore.snapshot()
    const stillValid = current !== null && user.memberships.some((m) => m.districtId === current)
    if (stillValid) return
    const stored = localStorage.getItem(storageKey(user.id))
    const next = resolveInitialActiveDistrict(user.memberships, stored)
    activeDistrictStore.set(next)
  }, [user])

  const setActiveDistrict = useCallback((id: string) => {
    activeDistrictStore.set(id)
    if (user) localStorage.setItem(storageKey(user.id), id)
  }, [user])

  return { activeDistrictId, setActiveDistrict }
}
